import classNames from 'classnames';
import {
  type FC,
  type JSX,
  type ReactNode,
  type Ref,
  createContext,
  memo,
  useContext,
} from 'react';
import type { IconGlyph, IconStyle } from './IconGlyph';

export type IconSize = '2xs' | 'xs' | 'sm' | 'base' | 'lg' | 'xl';

export type IconProps = JSX.IntrinsicElements['div'] & {
  className?: string;
  inline?: boolean;
  glyph: IconGlyph;
  size?: IconSize;
  wrapperRef?: Ref<HTMLDivElement>;
  iconStyle?: IconStyle;
  loading?: boolean;
  autoWidth?: boolean;
};

export type IconContextType = {
  iconSize: IconSize;
};

export const IconContext = createContext<IconContextType>({ iconSize: 'base' });

export const iconSizeClassName: { [Key in IconSize]: string } = {
  '2xs': 'text-icon-2xs',
  xs: 'text-icon-xs',
  sm: 'text-icon-sm',
  base: 'text-icon-base',
  lg: 'text-icon-lg',
  xl: 'text-icon-xl',
};

export const iconDimensionsClassName: {
  safe: { [Key in IconSize]: string };
  auto: { [Key in IconSize]: string };
} = {
  safe: {
    '2xs': 'size-3.5',
    xs: 'size-4',
    sm: 'size-5',
    base: 'size-6',
    lg: 'size-7',
    xl: 'size-7',
  },
  auto: {
    '2xs': 'h-3.5',
    xs: 'h-4',
    sm: 'h-5',
    base: 'h-6',
    lg: 'h-7',
    xl: 'h-7',
  },
};

const BareIcon: FC<IconProps> = ({
  className,
  inline,
  glyph,
  size,
  wrapperRef,
  iconStyle = 'regular',
  loading,
  autoWidth,
  ...otherProps
}: IconProps): ReactNode => {
  const iconSize = size ?? useContext(IconContext).iconSize;

  return (
    <span
      className={classNames(
        inline ? 'inline-flex' : 'flex',
        'shrink-0 grow-0 items-center justify-center',
        iconSizeClassName[iconSize],
        iconDimensionsClassName[autoWidth ? 'auto' : 'safe'][iconSize],
        className,
      )}
      ref={wrapperRef}
      {...otherProps}
    >
      <i
        className={classNames(
          loading ? 'fa-regular' : `fa-${iconStyle}`,
          loading ? 'fa-spinner fa-spin' : glyph,
        )}
      />
    </span>
  );
};

export const Icon = memo(BareIcon);
